import i18next from 'i18next';
import _ from 'lodash';
import moment from 'moment-timezone';
import { sqSummaryReportStore, sqWorksheetStore } from '@/core/core.stores';
import { InterpolationMethod } from '@/services/calculationRunner.constants';
import { primaryCharts, secondaryCharts } from '@/summaryReport/summaryData.types';
import {
  extractErrorMessage,
  fetchStatsSummaryData,
  formatRedactedInputsMessage,
  getOrderedChartKeys,
  isChartKey,
} from '@/summaryReport/summaryReport.utilities';
import { fetchSystemTemplate } from '@/tools/models/systemTemplate.api';
import { applyTemplateOnce, reapplyTemplate } from '@/tools/models/template.utilities';
import {
  ALIGNMENT_CONDITION_WEEKS,
  CONTROL_CHART_OUTPUTS,
  INPUT_NAMES,
  MODEL_NAME,
  OPERATIONALIZE_FIELD_LABEL_KEYS,
  OPERATIONALIZE_INPUT_FIELDS,
  OPERATIONALIZE_RESOLVE_ERROR_SOURCES,
  OUTPUT_GROUP_RULE_NAMES,
  type OperationalizeInputField,
  PRIMARY,
  PRIMARY_CHART_SLOT,
  RUN_RULE_OUTPUT_GROUP_KEYS,
  SECONDARY,
  SECONDARY_CHART_SLOT,
  STATISTIC_OUTPUT_FEATURE_NAMES,
  StatisticType,
  TIMESTAMP_DURATION,
  TIMESTAMP_END,
  TIMESTAMP_START,
} from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.constants';
import {
  applyLayoutPlan,
  buildLayoutPlan,
} from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.layout';
import { findItemIn, getAllItems, getTrendStores } from '@/trend/trendDataHelper.utilities';
import { findItem } from '@/trendData/itemStore.utilities';
import { removeGaps } from '@/trendData/yAxis.actions';
import { SUPPORT_LINK } from '@/utilities/externalLinks';
import { isForbidden } from '@/utilities/http.utilities';
import { decorateItemWithProperties } from '@/utilities/utilities';

import { sqFormulasApi } from '@seeqdev/api/endpoint/FormulasApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { SeeqNames } from '@seeqdev/constants';
import type { TableColumn } from '@/core/Table.atom';
import type {
  ChartKeys,
  PrimaryChartKeys,
  SecondaryChartKeys,
  StatisticsSummaryItem,
} from '@/summaryReport/summaryData.types';
import type { RedactedInputError } from '@/summaryReport/summaryReport.utilities';
import type {
  BaselineInputs,
  ChartSlot,
  ControlChartOutputGroupKey,
  ControlChartSelectedOutputs,
  InputFeatureRuntime,
  OperationalizeRehydratePayload,
  OperationalizeResolveError,
  OperationalizeResolveErrorInfo,
  OutputGroupRuleKey,
} from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.types';
import type { ToolItem } from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type { InputFeatureV1 } from '@seeqdev/api/model/InputFeatureV1';
import type { InstanceOutputMappingV1 } from '@seeqdev/api/model/InstanceOutputMappingV1';
import type { InstanceOutputV1 } from '@seeqdev/api/model/InstanceOutputV1';
import type { TemplateApplicationMappingV1 } from '@seeqdev/api/model/TemplateApplicationMappingV1';
import type { TemplateInitialApplicationInputV1 } from '@seeqdev/api/model/TemplateInitialApplicationInputV1';
import type { TemplateOutputV1 } from '@seeqdev/api/model/TemplateOutputV1';

export type TableItem = { id: string; [key: string]: any };

export type ModelTableData = {
  columns: TableColumn[];
  items: TableItem[];
};

interface BuildMapInput {
  name: string;
  inputSignal: ToolItem | null;
  baseline: StatisticsSummaryItem | null;
  condition: ToolItem | null;
  alignmentCondition: ToolItem | null;
  alignmentConditionFormula?: string;
  alignmentMaxDuration?: ValueWithUnitsItem;
  manualAlignmentSelected?: boolean;
  periodicCondition: ToolItem | null;
  timestampPlacement?: string;
  interpolationMethod?: InterpolationMethod;
  selectedStatistics?: StatisticType[];
  controlCharts?: string[];
  maxCapsuleDuration?: ValueWithUnitsItem;
  maxInterpolation?: ValueWithUnitsItem;
}

interface ExecutionInputs extends BuildMapInput {
  id?: string;
  model: TemplateOutputV1;
  workbookID: string;
  selectedOutputs: string[];
  selectedStatistics?: StatisticType[];
  controlCharts?: string[];
  maxCapsuleDuration?: ValueWithUnitsItem;
  alignmentMaxDuration?: ValueWithUnitsItem;
}

export const executeInputs = async ({
  id,
  name,
  inputSignal,
  baseline,
  condition,
  alignmentCondition,
  alignmentConditionFormula,
  alignmentMaxDuration,
  manualAlignmentSelected,
  periodicCondition,
  timestampPlacement,
  interpolationMethod,
  model,
  workbookID,
  selectedOutputs,
  selectedStatistics,
  controlCharts,
  maxCapsuleDuration,
  maxInterpolation,
}: ExecutionInputs): Promise<{
  allOutputs: InstanceOutputMappingV1[];
}> => {
  const maps = buildMaps({
    name,
    inputSignal,
    baseline,
    condition,
    alignmentCondition,
    alignmentConditionFormula,
    alignmentMaxDuration,
    manualAlignmentSelected,
    periodicCondition,
    timestampPlacement,
    interpolationMethod,
    selectedStatistics,
    controlCharts,
    maxCapsuleDuration,
    maxInterpolation,
  });

  const modelApplicationInput = buildModelApplicationInput({
    model,
    itemMap: maps.itemMap,
    textMap: maps.textMap,
    booleanMap: maps.booleanMap,
    numericMap: maps.numericMap,
    numericUomMap: maps.numericUomMap,
    workbookId: workbookID,
  });

  const instanceOutput = await applyModelAndAddOutputsToTrend({
    id,
    model,
    modelApplicationInput,
    selectedOutputs,
    interpolationMethod,
    timestampPlacement,
  });

  return {
    allOutputs: instanceOutput?.outputs ?? [],
  };
};

export function buildMaps({
  name,
  inputSignal,
  baseline,
  condition,
  alignmentCondition,
  alignmentConditionFormula,
  alignmentMaxDuration,
  manualAlignmentSelected,
  periodicCondition,
  timestampPlacement,
  interpolationMethod,
  selectedStatistics,
  controlCharts,
  maxCapsuleDuration,
  maxInterpolation,
}: BuildMapInput): {
  itemMap: Record<string, string>;
  textMap: Record<string, string>;
  booleanMap: Record<string, boolean>;
  numericMap: Record<string, number>;
  numericUomMap: Record<string, string>;
} {
  const itemMap: Record<string, string> = {};
  const textMap: Record<string, string> = {};
  const booleanMap: Record<string, boolean> = {};
  const numericMap: Record<string, number> = {};
  const numericUomMap: Record<string, string> = {};

  if (!baseline?.id) {
    throw new Error('Baseline is required to operationalize a Statistics summary');
  }

  if (inputSignal) {
    itemMap[INPUT_NAMES.signalInput] = inputSignal.id!;
  }
  if (baseline) {
    itemMap[INPUT_NAMES.baselineInput] = baseline.id!;
  }
  textMap[SeeqNames.Models.InstanceNameInputFeatureName] = name;
  if (condition) {
    itemMap[INPUT_NAMES.restrictOutputToConditionInput] = condition.id!;
  }
  if (periodicCondition) {
    itemMap[INPUT_NAMES.periodicConditionInput] = periodicCondition.id!;
  }
  if (alignmentCondition) {
    itemMap[INPUT_NAMES.alignmentConditionInput] = alignmentCondition.id!;
  } else if (alignmentConditionFormula) {
    textMap[INPUT_NAMES.alignmentConditionManualInput] = alignmentConditionFormula;
  }
  booleanMap[INPUT_NAMES.manualAlignmentSelectedInput] = !!manualAlignmentSelected;
  if (selectedStatistics && selectedStatistics.length > 0) {
    itemMap[INPUT_NAMES.produceStats] = baseline.id;

    if (timestampPlacement === TIMESTAMP_START) {
      itemMap[INPUT_NAMES.startKeyInput] = baseline.id;
    } else if (timestampPlacement === TIMESTAMP_END) {
      itemMap[INPUT_NAMES.endKeyInput] = baseline.id;
    } else if (timestampPlacement === TIMESTAMP_DURATION) {
      itemMap[INPUT_NAMES.durationKeyInput] = baseline.id;
    }

    if (timestampPlacement === TIMESTAMP_DURATION || interpolationMethod === InterpolationMethod.STEP) {
      itemMap[INPUT_NAMES.toStepInput] = baseline.id;
    } else if (interpolationMethod === InterpolationMethod.DISCRETE) {
      itemMap[INPUT_NAMES.toDiscreteInput] = baseline.id;
    }
  }
  if (controlCharts && controlCharts.length > 0) {
    if (controlCharts.some((chart) => primaryCharts.includes(chart as PrimaryChartKeys))) {
      itemMap[INPUT_NAMES.producePrimaryChart] = baseline.id!;
    }
    if (controlCharts.some((chart) => secondaryCharts.includes(chart as SecondaryChartKeys))) {
      itemMap[INPUT_NAMES.produceSecondaryChart] = baseline.id!;
    }
  }
  if (maxCapsuleDuration) {
    numericMap[INPUT_NAMES.removeLongerThan] = maxCapsuleDuration.value;
    numericUomMap[INPUT_NAMES.removeLongerThan] = maxCapsuleDuration.units;
  }
  if (maxInterpolation) {
    numericMap[INPUT_NAMES.maxInterpolationInput] = maxInterpolation.value;
    numericUomMap[INPUT_NAMES.maxInterpolationInput] = maxInterpolation.units;
  }
  if (alignmentMaxDuration) {
    numericMap[INPUT_NAMES.alignmentMaxDurationInput] = alignmentMaxDuration.value;
    numericUomMap[INPUT_NAMES.alignmentMaxDurationInput] = alignmentMaxDuration.units;
  }

  return { itemMap, textMap, booleanMap, numericMap, numericUomMap };
}

export function buildModelApplicationInput({
  model,
  itemMap,
  textMap,
  booleanMap,
  numericMap,
  numericUomMap,
  workbookId,
}: {
  model: TemplateOutputV1;
  itemMap: Record<string, string>;
  textMap: Record<string, string>;
  booleanMap: Record<string, boolean>;
  numericMap: Record<string, number>;
  numericUomMap: Record<string, string>;
  workbookId: string;
}): TemplateInitialApplicationInputV1 {
  const inputs: TemplateApplicationMappingV1[] = (model.inputFeatures ?? [])
    .map((feature: InputFeatureV1): TemplateApplicationMappingV1 => {
      const name = feature.name ?? '';
      return {
        featureId: feature.id,
        itemIdInput: itemMap[name],
        textInput: textMap[name],
        booleanInput: booleanMap[name],
        numericInput: numericMap[name],
        numericInputUom: numericUomMap[name],
      };
    })
    .filter(
      (inp: TemplateApplicationMappingV1) =>
        inp.itemIdInput !== undefined ||
        inp.textInput !== undefined ||
        inp.booleanInput !== undefined ||
        inp.numericInput !== undefined ||
        inp.numericInputUom !== undefined,
    );
  return {
    datasourceClass: SeeqNames.LocalDatasources.SeeqCalculationItemStorage.DatasourceClass,
    datasourceId: SeeqNames.LocalDatasources.SeeqCalculationItemStorage.DatasourceId,
    inputs,
    sourceId: workbookId,
  } as TemplateInitialApplicationInputV1;
}

const applyModelAndAddOutputsToTrend = async ({
  id,
  model,
  modelApplicationInput,
  selectedOutputs,
  interpolationMethod,
  timestampPlacement,
}: {
  id?: string;
  model: TemplateOutputV1;
  modelApplicationInput: TemplateInitialApplicationInputV1;
  selectedOutputs: string[];
  interpolationMethod?: InterpolationMethod;
  timestampPlacement?: string;
}) => {
  const instanceOutput = !_.isNil(id)
    ? await reapplyTemplate(model.id, id, modelApplicationInput)
    : await applyTemplateOnce(model.id, modelApplicationInput);

  removeGaps();

  const plan = buildLayoutPlan({
    model,
    instanceId: instanceOutput.instanceId!,
    outputs: instanceOutput.outputs!,
    selectedOutputs,
    interpolationMethod,
    timestampPlacement,
  });

  await applyLayoutPlan(plan);

  return instanceOutput;
};

export const getPrimarySecondaryFeatures = (spcModel: TemplateOutputV1, defaultFeatures?: string[]) => {
  const primaryFeatureIds: string[] = [];
  const secondaryFeatureIds: string[] = [];
  for (const feature of spcModel.derivedFeatures ?? []) {
    if (feature.id && (!defaultFeatures || defaultFeatures.includes(feature.name))) {
      const lower = (feature.name ?? '').toLowerCase();
      if (lower.startsWith(PRIMARY.toLowerCase())) primaryFeatureIds.push(feature.id);
      if (lower.startsWith(SECONDARY.toLowerCase())) secondaryFeatureIds.push(feature.id);
    }
  }
  return { primaryFeatureIds, secondaryFeatureIds };
};

export const extractStatisticOutputIds = (
  model: TemplateOutputV1,
  outputs: InstanceOutputMappingV1[],
): Partial<Record<StatisticType, string>> => {
  const outputIds: Partial<Record<StatisticType, string>> = {};

  const featureUuidToStatType: Record<string, StatisticType> = {};

  model.derivedFeatures?.forEach((feature) => {
    const featureName = feature.name ?? '';

    Object.entries(STATISTIC_OUTPUT_FEATURE_NAMES).forEach(([statType, expectedName]) => {
      if (featureName === expectedName) {
        featureUuidToStatType[feature.id!] = statType as StatisticType;
      }
    });
  });

  outputs.forEach((output: InstanceOutputMappingV1) => {
    const matchedStatType = featureUuidToStatType[output.featureId!];
    if (matchedStatType) {
      outputIds[matchedStatType] = output.outputId ?? '';
    }
  });

  return outputIds;
};

export const extractControlChartOutputIds = (
  model: TemplateOutputV1,
  outputs: InstanceOutputMappingV1[],
  controlCharts: string[],
): Record<string, Partial<Record<string, string>>> => {
  const result: Record<string, Partial<Record<string, string>>> = {};

  const slotToChartKey: Partial<Record<ChartSlot, string>> = {};
  for (const chartKey of controlCharts) {
    if (primaryCharts.includes(chartKey as PrimaryChartKeys)) {
      slotToChartKey[PRIMARY_CHART_SLOT] = chartKey;
    } else if (secondaryCharts.includes(chartKey as SecondaryChartKeys)) {
      slotToChartKey[SECONDARY_CHART_SLOT] = chartKey;
    }
  }

  const featureUuidToChartOutput: Record<string, { chartKey: string; outputKey: string }> = {};

  model.derivedFeatures?.forEach((feature) => {
    const featureName = feature.name ?? '';

    for (const [slot, chartKey] of Object.entries(slotToChartKey) as [ChartSlot, string][]) {
      const slotOutputs = CONTROL_CHART_OUTPUTS[slot];
      for (const [outputKey, config] of Object.entries(slotOutputs)) {
        if ('featureName' in config) {
          if (featureName === config.featureName) {
            featureUuidToChartOutput[feature.id!] = { chartKey, outputKey };
          }
        } else if ('featureNames' in config) {
          const idx = config.featureNames.indexOf(featureName);
          if (idx !== -1) {
            featureUuidToChartOutput[feature.id!] = { chartKey, outputKey: config.outputKeys[idx] };
          }
        }
      }
    }
  });

  outputs.forEach((output: InstanceOutputMappingV1) => {
    const match = featureUuidToChartOutput[output.featureId!];
    if (match && output.outputId) {
      if (!result[match.chartKey]) {
        result[match.chartKey] = {};
      }
      result[match.chartKey][match.outputKey] = output.outputId;
    }
  });

  return result;
};

export const fetchSPCModel = (): Promise<TemplateOutputV1> =>
  fetchSystemTemplate(MODEL_NAME, {
    notFound: `${MODEL_NAME} model not found`,
    multipleFound: `Multiple ${MODEL_NAME} models found. Cannot determine unique model.`,
  });

export const openKB = (anchor?: string) =>
  window.open(
    `${SUPPORT_LINK}/operationalize-statistic-summaries${anchor ? `#${anchor}` : ''}`,
    '_blank',
    'noopener,noreferrer',
  );

export function buildResolveErrorInfo(
  itemId: string,
  error: unknown,
  itemType?: string,
): OperationalizeResolveErrorInfo {
  return {
    itemId,
    itemType,
    source: OPERATIONALIZE_RESOLVE_ERROR_SOURCES.FIELD,
    isForbidden: isForbidden(error),
    message: extractErrorMessage(error),
  };
}

/**
 * Whether the card should show the baseline summary's own error. Hidden when the tool's error message already
 * reports the same failure. Either the summary itself could not be fetched, or it references an inaccessible input
 * whose access denial the tool the lists and would otherwise show twice.
 */
export function shouldShowBaselineItemError(resolveErrors: OperationalizeResolveError[]): boolean {
  const baselineFetchFailed = resolveErrors.some(
    (error) =>
      error.field === OPERATIONALIZE_INPUT_FIELDS.baseline &&
      error.source === OPERATIONALIZE_RESOLVE_ERROR_SOURCES.FIELD,
  );
  const referencesRedactedInput = resolveErrors.some(
    (error) =>
      error.isForbidden &&
      error.field !== OPERATIONALIZE_INPUT_FIELDS.baseline &&
      error.source === OPERATIONALIZE_RESOLVE_ERROR_SOURCES.FIELD,
  );
  return !baselineFetchFailed && !referencesRedactedInput;
}

/**
 * Turns resolve errors into the messages shown in the tool's error message. Field failures are labeled with
 * their field, except forbidden (access-denied) inputs, which are collapsed together with the baseline probe's
 * inaccessible inputs into a single message so the identity is stated once, not per item. A forbidden baseline
 * is never collapsed together with the forbidden inputs.
 */
export function buildResolveErrorMessages(
  resolveErrors: OperationalizeResolveError[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string[] {
  const fieldErrors = _.uniqBy(
    resolveErrors.filter((error) => error.source === OPERATIONALIZE_RESOLVE_ERROR_SOURCES.FIELD),
    'itemId',
  );
  const probedInputErrors = _.uniqBy(
    resolveErrors.filter(
      (error) =>
        error.source === OPERATIONALIZE_RESOLVE_ERROR_SOURCES.PROBE &&
        !fieldErrors.some((field) => field.itemId === error.itemId),
    ),
    'itemId',
  );

  const isRedactedInput = (error: OperationalizeResolveError): boolean =>
    error.isForbidden && error.field !== OPERATIONALIZE_INPUT_FIELDS.baseline;

  const messages = fieldErrors
    .filter((error) => !isRedactedInput(error))
    .map((error) =>
      t('INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.RESOLVE_ITEM_ERROR_DETAILED', {
        field: t(OPERATIONALIZE_FIELD_LABEL_KEYS[error.field]),
        itemId: error.itemId,
        message: error.message,
      }),
    );

  const redactedItems = [...fieldErrors.filter(isRedactedInput), ...probedInputErrors];
  if (redactedItems.length > 0) {
    messages.push(formatRedactedInputsMessage(redactedItems));
  }
  return messages;
}

/**
 * Thrown when an item backing an operationalize input cannot be fetched, identifying which input failed
 * and why.
 */
export class OperationalizeInputFetchError extends Error {
  readonly field: OperationalizeInputField;
  readonly itemId: string;
  readonly itemType?: string;
  readonly isForbidden: boolean;

  constructor(field: OperationalizeInputField, itemId: string, cause: unknown, itemType?: string) {
    super(extractErrorMessage(cause), { cause });
    this.name = 'OperationalizeInputFetchError';
    this.field = field;
    this.itemId = itemId;
    this.itemType = itemType;
    this.isForbidden = isForbidden(cause);
  }

  toResolveErrorInfo(): OperationalizeResolveErrorInfo {
    return {
      itemId: this.itemId,
      itemType: this.itemType,
      source: OPERATIONALIZE_RESOLVE_ERROR_SOURCES.FIELD,
      isForbidden: this.isForbidden,
      message: this.message,
      field: this.field,
    };
  }
}

/**
 * Fetches an item by ID and decorates it with metadata from the item. Returns the original item unchanged
 * when it has no id. Throws if the fetch fails.
 */
export async function fetchItemWithMetadata(item: ToolItem): Promise<ToolItem> {
  if (!item?.id) return item;
  const { data: fetchedItem } = await sqItemsApi.getItemAndAllProperties({ id: item.id });
  return decorateItemWithProperties(fetchedItem) as ToolItem;
}

const WEEKS_ALIGNMENT_PATTERN = new RegExp(`${_.escapeRegExp(ALIGNMENT_CONDITION_WEEKS)}\\(Day\\.\\w+,\\s*"[^"]*"\\)`);

export function buildWeeklyAlignmentFormula(timezoneName: string): string {
  return `${ALIGNMENT_CONDITION_WEEKS}(Day.Sunday, "${timezoneName}")`;
}

/**
 * Determines the appropriate alignment condition and formula based on a limiting condition.
 * Returns the alignment condition and formula to use:
 * - If limitCondition is provided: uses that condition with no formula
 * - If limitCondition is null: uses Weekly formula with no condition and worksheet timezone
 */
export function getAlignmentConditionFromLimitCondition(limitCondition: ToolItem | null): {
  alignmentCondition: ToolItem | null;
  alignmentConditionFormula: string | undefined;
} {
  if (limitCondition) {
    return {
      alignmentCondition: limitCondition,
      alignmentConditionFormula: undefined,
    };
  }
  return {
    alignmentCondition: null,
    alignmentConditionFormula: buildWeeklyAlignmentFormula(sqWorksheetStore.timezone.name),
  };
}

const SET_MAX_DURATION_PATTERN = /\$alignmentCondition\.setMaximumDuration\((\d+(?:\.\d+)?)\s*([a-zA-Z]+)\)/;

const BASELINE_PARAMETER_FIELDS: Record<string, OperationalizeInputField> = {
  signal: OPERATIONALIZE_INPUT_FIELDS.inputSignal,
  condition: OPERATIONALIZE_INPUT_FIELDS.condition,
  alignmentCondition: OPERATIONALIZE_INPUT_FIELDS.alignmentCondition,
};

/**
 * Fetches everything the operationalize tool needs from a baseline statistics summary. Inputs the user cannot
 * access are listed in `redactedInputs` and returned as their preview items marked `redacted`, so
 * `isItemRedacted` reports them without depending on the trend runtime store. Throws an
 * {@link OperationalizeInputFetchError} when the summary cannot be fetched or an input fails to fetch for a
 * non-access reason.
 */
export async function fetchBaselineInputs(baselineId: string): Promise<BaselineInputs> {
  const { data } = await sqFormulasApi.getItem({ id: baselineId }).catch((e) => {
    throw new OperationalizeInputFetchError(OPERATIONALIZE_INPUT_FIELDS.baseline, baselineId, e);
  });

  const uniqueInputs = _.uniqBy(_.compact(_.map(data.parameters, (parameter) => parameter.item)), 'id');
  const fieldFor = (itemId: string): OperationalizeInputField => {
    const name = _.find(data.parameters, (parameter) => parameter.item?.id === itemId)?.name ?? '';
    return BASELINE_PARAMETER_FIELDS[name] ?? OPERATIONALIZE_INPUT_FIELDS.baseline;
  };

  const resolvedById = new Map<string, ToolItem>();
  const redactedInputs: RedactedInputError[] = [];
  await Promise.all(
    uniqueInputs.map(async (input) => {
      try {
        resolvedById.set(input.id, await fetchItemWithMetadata(input as ToolItem));
      } catch (error) {
        if (!isForbidden(error)) {
          throw new OperationalizeInputFetchError(fieldFor(input.id), input.id, error, input.type);
        }
        redactedInputs.push({ itemId: input.id, itemType: input.type, message: extractErrorMessage(error) });
        resolvedById.set(input.id, { ...(input as ToolItem), redacted: true });
      }
    }),
  );

  const itemFor = (name: string): ToolItem | null => {
    const preview = _.find(data.parameters, (parameter) => parameter.name === name)?.item;
    return preview ? (resolvedById.get(preview.id) ?? null) : null;
  };
  const signal = itemFor('signal');
  const condition = itemFor('condition');
  const alignmentCondition = itemFor('alignmentCondition');

  let alignmentConditionFormula: string | undefined;
  let alignmentMaxDuration: ValueWithUnitsItem | undefined;
  if (!alignmentCondition?.id && data.formula) {
    alignmentConditionFormula = data.formula.match(WEEKS_ALIGNMENT_PATTERN)?.[0];
  }
  if (alignmentCondition?.id && data.formula) {
    const durationMatch = data.formula.match(SET_MAX_DURATION_PATTERN);
    if (durationMatch) {
      alignmentMaxDuration = { value: parseFloat(durationMatch[1]), units: durationMatch[2] };
    }
  }

  return {
    baselineId,
    signal,
    condition,
    alignmentCondition,
    alignmentConditionFormula,
    alignmentMaxDuration,
    redactedInputs,
  };
}

export function hasBaselineDisplayData(item: StatisticsSummaryItem): boolean {
  return !!item.name && !!item.data?.inputs;
}

/**
 * Fetches the display data for a baseline statistics summary item.
 * Retrieves both the item name (via getItem) and the full stats data (via experimental endpoint),
 * then merges them into the existing baseline.
 */
export async function fetchBaselineDisplayData(baseline: StatisticsSummaryItem): Promise<StatisticsSummaryItem> {
  const [{ data: itemData }, { data }] = await Promise.all([
    sqFormulasApi.getItem({ id: baseline.id }),
    fetchStatsSummaryData(baseline.id),
  ]);

  return {
    ...baseline,
    data,
    name: itemData.name ?? baseline.name,
  } as StatisticsSummaryItem;
}

/**
 * Extracts max duration from model instance inputs
 */
export function extractMaxDurationFromInputs(
  inputs: TemplateApplicationMappingV1[],
  inputFeatures: InputFeatureV1[],
): ValueWithUnitsItem | undefined {
  return extractNumericInput(inputs, inputFeatures, INPUT_NAMES.removeLongerThan);
}

export function extractMaxInterpolationFromInputs(
  inputs: TemplateApplicationMappingV1[],
  inputFeatures: InputFeatureV1[],
): ValueWithUnitsItem | undefined {
  return extractNumericInput(inputs, inputFeatures, INPUT_NAMES.maxInterpolationInput);
}

export function extractAlignmentMaxDurationFromInputs(
  inputs: TemplateApplicationMappingV1[],
  inputFeatures: InputFeatureV1[],
): ValueWithUnitsItem | undefined {
  return extractNumericInput(inputs, inputFeatures, INPUT_NAMES.alignmentMaxDurationInput);
}

function extractNumericInput(
  inputs: TemplateApplicationMappingV1[],
  inputFeatures: InputFeatureV1[],
  targetName: string,
): ValueWithUnitsItem | undefined {
  for (const input of inputs) {
    const feature = inputFeatures.find((f) => f.id === input.featureId);
    const isTargetFeature = feature?.name === targetName;
    const hasValidNumeric = input.numericInput != null && _.isFinite(input.numericInput);
    if (isTargetFeature && hasValidNumeric && input.numericInputUom) {
      return { value: input.numericInput!, units: input.numericInputUom };
    }
  }
  return undefined;
}

export function getChartSlot(chartKey: string, orderedChartKeys: string[]): ChartSlot | null {
  if (chartKey === orderedChartKeys[0]) return PRIMARY_CHART_SLOT;
  if (chartKey === orderedChartKeys[1]) return SECONDARY_CHART_SLOT;
  return null;
}

const NON_DEFAULT_OUTPUT_GROUPS = new Set(['OneSigmaOutput', 'TwoSigmaOutput', 'OverallRuleViolationOutput']);

export function getDefaultOutputGroups(slot: ChartSlot): string[] {
  return Object.keys(CONTROL_CHART_OUTPUTS[slot]).filter((key) => !NON_DEFAULT_OUTPUT_GROUPS.has(key));
}

export function getFeatureNamesForOutputGroups(slot: ChartSlot, selectedGroups: string[]): string[] {
  const slotOutputs = CONTROL_CHART_OUTPUTS[slot];
  const featureNames: string[] = [];
  for (const groupKey of selectedGroups) {
    const config = slotOutputs[groupKey as keyof typeof slotOutputs];
    if (config) {
      if ('featureNames' in config) {
        featureNames.push(...config.featureNames);
      } else if ('featureName' in config) {
        featureNames.push(config.featureName);
      }
    }
  }
  return featureNames;
}

/**
 * Maps old chart key selections to new baseline chart keys by slot position.
 * Output group keys (SignalOutput, CenterlineOutput, etc.) are slot-based
 * concepts that transfer across chart types, so only the chart key changes.
 */
export function remapChartsToNewBaseline(
  oldCharts: string[],
  oldOrderedKeys: string[],
  newOrderedKeys: string[],
): string[] {
  const mappedCharts: string[] = [];
  for (const oldKey of oldCharts) {
    const slot = getChartSlot(oldKey, oldOrderedKeys);
    if (slot === PRIMARY_CHART_SLOT && newOrderedKeys[0]) {
      mappedCharts.push(newOrderedKeys[0]);
    } else if (slot === SECONDARY_CHART_SLOT && newOrderedKeys[1]) {
      mappedCharts.push(newOrderedKeys[1]);
    }
  }
  return mappedCharts;
}

/**
 * Re-keys output group selections from old chart keys to new chart keys.
 * The output group keys themselves (SignalOutput, CenterlineOutput, etc.) are
 * preserved because they are slot-based, not chart-type-specific.
 */
export function remapOutputGroupSelections(
  oldSelections: Record<string, string[]>,
  oldCharts: string[],
  oldOrderedKeys: string[],
  newOrderedKeys: string[],
): Record<string, string[]> {
  const remapped: Record<string, string[]> = {};
  for (const oldKey of oldCharts) {
    const slot = getChartSlot(oldKey, oldOrderedKeys);
    if (slot) {
      const newKey = slot === PRIMARY_CHART_SLOT ? newOrderedKeys[0] : newOrderedKeys[1];
      if (newKey && oldSelections[oldKey]) {
        remapped[newKey] = oldSelections[oldKey];
      }
    }
  }
  return remapped;
}

export function isOutputGroupRuleKey(key: string): key is OutputGroupRuleKey {
  return key in OUTPUT_GROUP_RULE_NAMES;
}

/**
 * Determines whether a statistic can be computed from the baseline's stats. MEAN_PVALUE and VARIANCE_PVALUE
 * have different requirements: the backend behind VARIANCE_PVALUE explicitly rejects a zero stdDev,
 * while the backend behind MEAN_PVALUE does not, so only VARIANCE_PVALUE checks for a non-zero stdDev.
 */
export function isStatisticAvailable(
  statType: StatisticType,
  baseline: StatisticsSummaryItem | null | undefined,
): boolean {
  const stats = baseline?.data?.stats;
  const hasEnoughSamples = (stats?.count ?? 0) >= 2;

  switch (statType) {
    case StatisticType.CPK:
      return stats?.cpk != null;
    case StatisticType.PPK:
      return stats?.ppk != null;
    case StatisticType.P_VALUE:
      return stats?.pValue != null;
    case StatisticType.MEAN_PVALUE:
      return stats?.sampleMean != null && stats?.stdDev != null && hasEnoughSamples;
    case StatisticType.VARIANCE_PVALUE:
      return stats?.stdDev != null && stats.stdDev !== 0 && hasEnoughSamples;
    default:
      return false;
  }
}

/**
 * Filters output groups to exclude run rule groups whose rules are not
 * enabled in the baseline's SPC chart configuration.
 */
export function filterOutputGroupsByEnabledRules(groups: string[], enabledRules: string[]): string[] {
  const enabledRuleSet = new Set(enabledRules);
  return groups.filter((group) => {
    if (!RUN_RULE_OUTPUT_GROUP_KEYS.has(group)) return true;
    if (enabledRuleSet.size === 0) return false;
    if (isOutputGroupRuleKey(group)) return enabledRuleSet.has(OUTPUT_GROUP_RULE_NAMES[group]);
    return true;
  });
}

/**
 * Generates a default name for operationalize statistics summary with current timestamp.
 * Uses the worksheet timezone to format the timestamp.
 */
export function getDefaultOperationalizeName(translationFunction: (key: string) => string): string {
  const timestamp = moment().tz(sqWorksheetStore.timezone.name).format('l LT');
  return `${translationFunction('INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.DEFAULT_NAME_PREFIX')} ${timestamp}`;
}

/**
 * Converts an item with an optional id property (or null/undefined) to its ID string (or null).
 * Useful for storing item references by ID in the store.
 */
export const toItemId = (item: { id?: string } | null | undefined): string | null => item?.id ?? null;

/**
 * Selections derived from the trend. A chart occupies either the primary or secondary slot, but its type
 * (I/XBAR, MR/R/S) comes from the baseline, not the instance. Output groups are keyed by slot here and
 * the caller remaps them to the actual chart keys once the baseline is known.
 */
export interface DerivedSelectionsFromTrend {
  slotSelectedOutputs: Partial<Record<ChartSlot, ControlChartOutputGroupKey[]>>;
  selectedStatistics: StatisticType[];
}

/**
 * Reconstructs the selected control-chart output groups and statistics from what is currently displayed on
 * the trend. Only instance outputs whose created item is present on the trend count as selected, since the
 * trend is the live representation of the user's selection. Execute adds/removes trend items to match the
 * control-chart outputs and statistics selected by the user on the Operationalize tool.
 *
 * Groups are keyed by chart *slot*, because that is all the instance/model encode — the produced outputs
 * distinguish primaryChart* vs secondaryChart* features but never the chart type. Use
 * {@link mapSlotSelectionsToChartKeys} to convert to the chart-key-keyed shape the store/organism use once
 * the baseline's chart order is known.
 */
export function deriveSelectionsFromTrend(
  modelData: TemplateOutputV1 | undefined,
  instanceData: InstanceOutputV1 | undefined,
): DerivedSelectionsFromTrend {
  const result: DerivedSelectionsFromTrend = {
    slotSelectedOutputs: {},
    selectedStatistics: [],
  };
  if (!modelData?.derivedFeatures || !instanceData?.outputs) {
    return result;
  }

  const trendItemIds = new Set<string>(getAllItems({}).map((item) => item.id));

  const featureIdToName: Record<string, string> = {};
  for (const feature of modelData.derivedFeatures) {
    if (feature.id && feature.name) {
      featureIdToName[feature.id] = feature.name;
    }
  }

  const displayedFeatureNames = new Set<string>();
  for (const output of instanceData.outputs) {
    if (output.featureId && output.outputId && trendItemIds.has(output.outputId)) {
      const name = featureIdToName[output.featureId];
      if (name) {
        displayedFeatureNames.add(name);
      }
    }
  }

  for (const slot of [PRIMARY_CHART_SLOT, SECONDARY_CHART_SLOT] as ChartSlot[]) {
    const slotOutputs = CONTROL_CHART_OUTPUTS[slot];
    const groups: ControlChartOutputGroupKey[] = [];
    for (const [groupKey, config] of Object.entries(slotOutputs)) {
      if ('featureName' in config && displayedFeatureNames.has(config.featureName)) {
        groups.push(groupKey as ControlChartOutputGroupKey);
      } else if (
        'featureNames' in config &&
        config.featureNames.some((name: string) => displayedFeatureNames.has(name))
      ) {
        groups.push(groupKey as ControlChartOutputGroupKey);
      }
    }
    if (groups.length > 0) {
      result.slotSelectedOutputs[slot] = groups;
    }
  }

  for (const [statType, featureName] of Object.entries(STATISTIC_OUTPUT_FEATURE_NAMES)) {
    if (displayedFeatureNames.has(featureName)) {
      result.selectedStatistics.push(statType as StatisticType);
    }
  }

  return result;
}

/**
 * Converts slot-keyed selections from {@link deriveSelectionsFromTrend} into the chart-key-keyed shape the
 * store and organism use, given the baseline's ordered chart keys (primary is index 0, secondary is index 1).
 */
export function mapSlotSelectionsToChartKeys(
  slotSelectedOutputs: Partial<Record<ChartSlot, ControlChartOutputGroupKey[]>>,
  orderedChartKeys: ChartKeys[],
): ControlChartSelectedOutputs {
  const result: ControlChartSelectedOutputs = {};
  const primaryKey = orderedChartKeys[0];
  const secondaryKey = orderedChartKeys[1];
  if (primaryKey && slotSelectedOutputs[PRIMARY_CHART_SLOT]) {
    result[primaryKey] = slotSelectedOutputs[PRIMARY_CHART_SLOT];
  }
  if (secondaryKey && slotSelectedOutputs[SECONDARY_CHART_SLOT]) {
    result[secondaryKey] = slotSelectedOutputs[SECONDARY_CHART_SLOT];
  }
  return result;
}

/**
 * Synchronous store-only lookup for a signal/condition: returns the trend item when it is already present
 * with the metadata the tool needs, otherwise null.
 */
export function findStoreItem(id: string | null): ToolItem | null {
  if (!id) {
    return null;
  }
  const trendItem = findItemIn(getTrendStores(), id) as ToolItem | undefined;
  if (trendItem && (trendItem.signalMetadata || trendItem.conditionMetadata)) {
    return trendItem;
  }
  return null;
}

/**
 * Resolves a signal or condition (with metadata) for an id. Prefers the item already on the trend when it
 * carries metadata, otherwise fetches it. Returns null for no id. Throws if the fetch fails.
 */
export async function resolveItem(id: string | null): Promise<ToolItem | null> {
  if (!id) {
    return null;
  }
  const storeItem = findStoreItem(id);
  if (storeItem) {
    return storeItem;
  }
  return fetchItemWithMetadata({ id });
}

/**
 * Synchronous store-only lookup for the baseline: returns the summary-report store copy when it already has
 * display data, otherwise null.
 */
export function findStoreBaseline(id: string | null): StatisticsSummaryItem | null {
  if (!id) {
    return null;
  }
  const storeItem = findItem(sqSummaryReportStore.getState().items, id) as StatisticsSummaryItem | undefined;
  return storeItem && hasBaselineDisplayData(storeItem) ? storeItem : null;
}

/**
 * Resolves the full baseline statistics summary (including display data such as spcCharts) for an id.
 * Prefers the in-memory summary-report store copy when it already has display data, otherwise fetches it so
 * the baseline resolves even when the summary is not on the worksheet. Returns null for no id. Throws if the
 * fetch fails.
 */
export async function resolveBaseline(id: string | null): Promise<StatisticsSummaryItem | null> {
  if (!id) {
    return null;
  }
  const storeItem = findStoreBaseline(id);
  if (storeItem) {
    return storeItem;
  }
  return fetchBaselineDisplayData({ id } as StatisticsSummaryItem);
}

/**
 * Reconstructs the chart-key-keyed selected outputs and statistics for an operationalize being edited.
 *
 * Runs as a one-shot at load time (not reactively) so the seeded selections become the user's working draft
 * and are never re-derived/clobbered until Execute. Fetches the baseline's spcCharts to resolve the
 * slot->chart-key mapping the instance data cannot provide, so it works even when the summary is not yet on
 * the worksheet. Returns empty selections if the baseline cannot be resolved.
 */
export async function reconstructOperationalizeChartSelections(
  modelData: TemplateOutputV1 | undefined,
  instanceData: InstanceOutputV1 | undefined,
): Promise<{ controlChartSelectedOutputs: ControlChartSelectedOutputs; selectedStatistics: StatisticType[] }> {
  const { slotSelectedOutputs, selectedStatistics } = deriveSelectionsFromTrend(modelData, instanceData);

  const baselineId =
    (modelData?.inputFeatures as InputFeatureRuntime[] | undefined)?.find(
      (feature) => feature.name === INPUT_NAMES.baselineInput,
    )?.inputItem ?? null;

  const baseline = await resolveBaseline(baselineId);
  const chartKeys = baseline?.data?.spcCharts ? Object.keys(baseline.data.spcCharts).filter(isChartKey) : [];
  const orderedChartKeys: ChartKeys[] = getOrderedChartKeys(chartKeys);

  return {
    controlChartSelectedOutputs: mapSlotSelectionsToChartKeys(slotSelectedOutputs, orderedChartKeys),
    selectedStatistics,
  };
}

export function extractRehydrateStateFromModelRehydratePayload(payload: OperationalizeRehydratePayload) {
  const inputFeatures = payload.modelData?.inputFeatures ?? [];
  const byName = new Map(inputFeatures.map((f: InputFeatureRuntime) => [f.name ?? '', f]));
  const nameFeature = byName.get(SeeqNames.Models.InstanceNameInputFeatureName);
  const alignmentItem = byName.get(INPUT_NAMES.alignmentConditionInput);
  const alignmentManual = byName.get(INPUT_NAMES.alignmentConditionManualInput);

  let name: string | undefined;
  if (nameFeature) {
    const copy = i18next.t('COPY');
    name = payload.instanceId ? String(nameFeature.inputText ?? '') : `${nameFeature.inputText ?? ''} (${copy})`;
  }

  let timestampPlacement: typeof TIMESTAMP_START | typeof TIMESTAMP_END | typeof TIMESTAMP_DURATION | undefined;
  if (byName.get(INPUT_NAMES.startKeyInput)?.inputItem) {
    timestampPlacement = TIMESTAMP_START;
  } else if (byName.get(INPUT_NAMES.endKeyInput)?.inputItem) {
    timestampPlacement = TIMESTAMP_END;
  } else if (byName.get(INPUT_NAMES.durationKeyInput)?.inputItem) {
    timestampPlacement = TIMESTAMP_DURATION;
  }

  let interpolationMethod: InterpolationMethod | undefined;
  if (byName.get(INPUT_NAMES.produceStats)?.inputItem) {
    if (byName.get(INPUT_NAMES.toDiscreteInput)?.inputItem) {
      interpolationMethod = InterpolationMethod.DISCRETE;
    } else if (byName.get(INPUT_NAMES.toStepInput)?.inputItem) {
      interpolationMethod = InterpolationMethod.STEP;
    } else {
      interpolationMethod = InterpolationMethod.LINEAR;
    }
  }

  let alignmentConditionFormula: string | undefined;
  if (!alignmentItem?.inputItem) {
    alignmentConditionFormula = alignmentManual?.inputText ?? undefined;
  }

  const id = payload.instanceId;
  const baselineId = byName.get(INPUT_NAMES.baselineInput)?.inputItem ?? null;
  const conditionId = byName.get(INPUT_NAMES.restrictOutputToConditionInput)?.inputItem ?? null;
  const alignmentConditionId = alignmentItem?.inputItem ?? null;
  const inputSignalId = byName.get(INPUT_NAMES.signalInput)?.inputItem ?? null;
  const periodicConditionId = byName.get(INPUT_NAMES.periodicConditionInput)?.inputItem ?? null;
  const manualAlignmentSelected = byName.get(INPUT_NAMES.manualAlignmentSelectedInput)?.inputBoolean ?? false;
  const controlChartSelectedOutputs =
    payload.controlChartSelectedOutputs !== undefined
      ? { controlChartSelectedOutputs: payload.controlChartSelectedOutputs }
      : {};
  const selectedStatistics =
    payload.selectedStatistics !== undefined ? { selectedStatistics: payload.selectedStatistics } : {};

  return {
    id,
    name,
    inputSignalId,
    conditionId,
    baselineId,
    periodicConditionId,
    alignmentConditionId,
    alignmentConditionFormula,
    manualAlignmentSelected,
    timestampPlacement,
    interpolationMethod,
    ...controlChartSelectedOutputs,
    ...selectedStatistics,
  };
}
